import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
    Plus, Pencil, Trash, Key, ShieldCheck, User as UserIcon, EnvelopeSimple, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyForm = { email: "", name: "", role: "user" };

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(null); // user id we're acting on

    const load = async () => {
        try {
            const r = await api.get("/admin/users");
            setUsers(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openInvite = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    const openEdit = (u) => {
        setEditing(u);
        setForm({ email: u.email, name: u.name, role: u.role });
        setOpen(true);
    };

    const save = async () => {
        try {
            if (editing) {
                await api.patch(`/admin/users/${editing.id}`, {
                    name: form.name,
                    role: form.role,
                });
                toast.success(`${form.email} updated`);
            } else {
                const r = await api.post("/admin/users", {
                    email: form.email.toLowerCase().trim(),
                    name: form.name,
                    role: form.role,
                });
                toast.success(`Invite sent to ${r.data.email}`, {
                    description: "They'll receive an email to set their own password.",
                });
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { active: !u.active });
            toast.success(`${u.email} ${u.active ? "deactivated" : "activated"}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toggle failed");
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success(`${u.email} deleted`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const resendInvite = async (u) => {
        setBusy(u.id);
        try {
            await api.post(`/admin/users/${u.id}/resend-invite`);
            toast.success(`Invite re-sent to ${u.email}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Resend failed");
        } finally {
            setBusy(null);
        }
    };

    const sendReset = async (u) => {
        if (!window.confirm(`Email a password-reset link to ${u.email}? Their current password will keep working until they use the new link.`)) return;
        setBusy(u.id);
        try {
            await api.post(`/admin/users/${u.id}/send-reset`);
            toast.success(`Reset link sent to ${u.email}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Reset failed");
        } finally {
            setBusy(null);
        }
    };

    if (loading) return <div className="px-8 lg:px-14 pt-16 text-sm text-muted-foreground">Loading users…</div>;

    return (
        <>
            <header className="px-8 lg:px-14 pt-12 pb-6 border-b border-border">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="overline mb-3">HAEVN Labs · Admin</div>
                        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Users</h1>
                        <p className="editorial text-muted-foreground mt-2 max-w-[68ch]">
                            Invite people to HAEVN Labs by email. They'll receive a one-time link to set their own password.
                            Admins have full operational rights; Users have read access to the intelligence views plus full
                            rights on Insights and Content Ideas.
                        </p>
                    </div>
                    <Button data-testid="invite-user-btn" onClick={openInvite} className="rounded-sm">
                        <EnvelopeSimple size={14} className="mr-1.5" weight="duotone" /> Invite user
                    </Button>
                </div>
            </header>

            <main className="px-8 lg:px-14 py-12 max-w-[1100px] mx-auto">
                <div className="border border-border bg-card">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="text-left overline text-[9px] p-4">User</th>
                                <th className="text-left overline text-[9px] p-4">Role</th>
                                <th className="text-left overline text-[9px] p-4">Status</th>
                                <th className="text-left overline text-[9px] p-4">Created</th>
                                <th className="text-right overline text-[9px] p-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border last:border-b-0">
                                    <td className="p-4">
                                        <div className="font-display font-semibold text-sm">{u.name || u.email.split("@")[0]}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
                                    </td>
                                    <td className="p-4">
                                        <Badge variant={u.role === "admin" ? "default" : "outline"} className="rounded-sm font-mono text-[10px] uppercase tracking-wider">
                                            {u.role === "admin" ? <ShieldCheck size={10} className="mr-1" /> : <UserIcon size={10} className="mr-1" />}
                                            {u.role}
                                        </Badge>
                                    </td>
                                    <td className="p-4">
                                        {u.invite_pending ? (
                                            <span data-testid={`status-pending-${u.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-primary">
                                                <Clock size={10} weight="duotone" /> Invite pending
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                data-testid={`toggle-active-${u.id}`}
                                                onClick={() => toggleActive(u)}
                                                disabled={u.id === me?.id}
                                                className={`text-[10px] font-mono uppercase tracking-wider border-b pb-0.5 ${u.active ? "border-emerald-500 text-emerald-700" : "border-accent text-accent"} disabled:opacity-50`}
                                                title={u.id === me?.id ? "You cannot deactivate yourself" : ""}
                                            >
                                                {u.active ? "Active" : "Suspended"}
                                            </button>
                                        )}
                                    </td>
                                    <td className="p-4 text-xs font-mono text-muted-foreground">
                                        {(u.created_at || "").slice(0, 10)}
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="inline-flex gap-1">
                                            <Button
                                                data-testid={`edit-user-${u.id}`}
                                                variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0"
                                                onClick={() => openEdit(u)} title="Edit name and role"
                                            >
                                                <Pencil size={11} />
                                            </Button>
                                            {u.invite_pending ? (
                                                <Button
                                                    data-testid={`resend-invite-${u.id}`}
                                                    variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0"
                                                    onClick={() => resendInvite(u)}
                                                    disabled={busy === u.id}
                                                    title="Resend invite email"
                                                >
                                                    <EnvelopeSimple size={11} weight="duotone" />
                                                </Button>
                                            ) : (
                                                <Button
                                                    data-testid={`send-reset-${u.id}`}
                                                    variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0"
                                                    onClick={() => sendReset(u)}
                                                    disabled={busy === u.id}
                                                    title="Email a password-reset link"
                                                >
                                                    <Key size={11} />
                                                </Button>
                                            )}
                                            <Button
                                                data-testid={`delete-user-${u.id}`}
                                                variant="ghost" size="sm" className="rounded-sm h-7 w-7 p-0"
                                                onClick={() => remove(u)}
                                                disabled={u.id === me?.id}
                                                title={u.id === me?.id ? "You cannot delete yourself" : "Delete user"}
                                            >
                                                <Trash size={11} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Permission matrix reminder */}
                <section className="mt-10 max-w-[68ch]">
                    <div className="overline mb-3">Permission matrix</div>
                    <table className="w-full text-[12px] border border-border">
                        <thead className="border-b border-border">
                            <tr>
                                <th className="text-left p-3 overline text-[9px]">Page</th>
                                <th className="text-center p-3 overline text-[9px]">Admin</th>
                                <th className="text-center p-3 overline text-[9px]">User</th>
                            </tr>
                        </thead>
                        <tbody className="font-mono">
                            {[
                                ["Briefing",        "View + re-synthesize",  "View only"],
                                ["Weekly Brief",    "View + generate",       "View only"],
                                ["Analyses",        "Full",                  "View"],
                                ["Insights",        "Full + approve",        "View + approve"],
                                ["Content Ideas",   "Full",                  "Full"],
                                ["Narrative Labs",  "Full",                  "View"],
                                ["Trends",          "Full",                  "View"],
                                ["Streams",         "Full",                  "View (listening map)"],
                                ["Raw Signals",     "Full",                  "—"],
                                ["Manual Sources",  "Full",                  "—"],
                                ["Workflow",        "Full",                  "—"],
                                ["Integrations",    "Full",                  "—"],
                                ["Admin Panel",     "Full",                  "—"],
                            ].map(([page, admin, user]) => (
                                <tr key={page} className="border-b border-border last:border-b-0">
                                    <td className="p-2.5">{page}</td>
                                    <td className="p-2.5 text-center text-emerald-700">{admin}</td>
                                    <td className={`p-2.5 text-center ${user === "—" ? "text-muted-foreground" : "text-foreground"}`}>{user}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            </main>

            {/* Invite / Edit dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm max-w-md" data-testid="user-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Edit user" : "Invite user"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="overline text-[10px] mb-1.5 block">Email</Label>
                            <Input
                                type="email"
                                data-testid="user-email-input"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                disabled={!!editing}
                                placeholder="you@example.com"
                                className="rounded-sm"
                            />
                        </div>
                        <div>
                            <Label className="overline text-[10px] mb-1.5 block">Name</Label>
                            <Input
                                data-testid="user-name-input"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Optional"
                                className="rounded-sm"
                            />
                        </div>
                        <div>
                            <Label className="overline text-[10px] mb-1.5 block">Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="user-role-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="user">User — read intelligence + edit Insights/Ideas</SelectItem>
                                    <SelectItem value="admin">Admin — full operational rights</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {!editing && (
                            <p className="text-[11px] text-muted-foreground italic editorial leading-relaxed">
                                We'll email this person a one-time link (valid 72 hours) to set their own password.
                                You won't need to share credentials.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                        <Button onClick={save} data-testid="save-user-btn" className="rounded-sm">
                            {editing ? "Save" : "Send invite"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
